import { App } from '@capacitor/app';
import { parseDeepLink } from './deepLink';
import { isNative } from './platform';
import { navigate } from './router';

/**
 * Bridges iOS's URL delivery into the app's router (spec §6). A no-op on
 * web, where the browser has already put the document on the right URL.
 *
 * Two sources, because neither covers both cases alone: `appUrlOpen` fires
 * when a link is opened while the app is already running, and `getLaunchUrl`
 * reports the URL a cold start was launched with — which iOS may have
 * delivered before any JS listener could exist. Both can fire for the same
 * launch; they are not de-duplicated. The router's guard compares against
 * the full `location.pathname + location.hash`, so a double fire that
 * resolves to the identical hash-bearing path is a no-op; only a double fire
 * whose two calls carry differing hashes still pushes two history entries.
 * Harmless today only because the app never drives history.back(); add
 * dedupe here before introducing any in-app back navigation.
 *
 * Every plugin call is fire-and-forget: a bridge failure must leave the app
 * playable on today's puzzle, not wedge the boot (spec §7).
 *
 * Returns a teardown so tests can unregister. The app never calls it — the
 * listener lives as long as the process.
 */
export function startNativeBridge(go: (path: string) => void = navigate): () => void {
  if (!isNative()) return () => {};

  let handle: { remove: () => Promise<void> } | null = null;
  let stopped = false;

  const open = (url: string | null | undefined): void => {
    if (!url) return;
    const path = parseDeepLink(url);
    if (path !== null) go(path);
  };

  void App.addListener('appUrlOpen', (event) => open(event.url))
    .then((registered) => {
      // Teardown can win the race against registration.
      if (stopped) void registered.remove();
      else handle = registered;
    })
    .catch(() => {});

  void App.getLaunchUrl()
    .then((launch) => open(launch?.url))
    .catch(() => {});

  return () => {
    stopped = true;
    void handle?.remove();
    handle = null;
  };
}
